import React, { useCallback, useMemo } from 'react';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import type {
  ProductCarouselNavigatePayload,
  ProductCarouselProductPayload,
} from '@noths/polaris-design-system-candidate-components';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import type { TrackingProductData } from 'src/tracking/common/products';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import type { TrackingContext } from 'src/types/TrackingContext';
import { useListingsRecommendations } from './hooks/useListingsRecommendations';

export interface RecommendedProductsProps {
  currency: CurrencyCode;
  deliveryZone?: DeliveryZoneCode;
  heading: string;
  servingConfig: RecommendationsServingConfig;
  trackingPosition: number;
  trackingTitle: string;
}

export const RecommendedProducts = ({
  currency,
  deliveryZone,
  heading,
  servingConfig,
  trackingPosition,
  trackingTitle,
}: RecommendedProductsProps) => {
  const trackingContentType = 'Listings recommendations';

  const trackingFullTitle = useMemo(
    () => `${trackingTitle} | ${servingConfig}`,
    [trackingTitle, servingConfig],
  );

  const trackingInfo: TrackingContext = useMemo(
    () => ({
      trackingTitle: trackingFullTitle,
      trackingContentType,
      trackingPosition,
    }),
    [trackingFullTitle, trackingPosition],
  );

  const { productsToTrack, recommendedProducts } = useListingsRecommendations({
    currency,
    deliveryZone,
    servingConfig,
  });

  const numberOfItems = useMemo(() => productsToTrack.length, [productsToTrack]);

  const onNavigate = useCallback(
    ({ direction, method }: ProductCarouselNavigatePayload) => {
      const trackingDirection = direction === 'next' ? 'next' : 'previous';

      trackCarouselNavigation(method === 'swipe', trackingDirection, {
        ...trackingInfo,
        numberOfItems,
      });
    },
    [trackingInfo, numberOfItems],
  );

  /* The payload's own `product` is the OOUX shape the card renders, which does
     not carry what tracking needs (the product code, the partner shortcode,
     `freeDomesticDelivery`, `salePercentage`), so the index is used to look the
     service product back up. */
  const onProductClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      { index: currentIndex }: ProductCarouselProductPayload,
    ) => {
      if (numberOfItems > 0) {
        const currentProduct: TrackingProductData | undefined = productsToTrack.find(
          (_, index) => index === currentIndex,
        );
        if (currentProduct) {
          trackProductClick(e, {
            ...trackingInfo,
            numberOfItems,
            product: currentProduct,
            productIndex: currentIndex,
          });
        }
      }
    },
    [numberOfItems, productsToTrack, trackingInfo],
  );

  /* One impression event for the whole carousel, listing every product, rather
     than the per-product `onProductVisible`. Keeps the GA payload identical to
     the Ribbons carousel this replaces. */
  const onVisible = useCallback(() => {
    if (numberOfItems > 0) {
      trackProductImpression({
        ...trackingInfo,
        products: productsToTrack,
      });
    }
  }, [numberOfItems, trackingInfo, productsToTrack]);

  // Delegate the loading/empty/loaded states to ProductCarousel: it renders a skeleton for `null`
  // (reserving layout space while the client fetch is in flight), nothing for an empty array, and the
  // carousel once products are available. This avoids a 0px-to-carousel jump (CLS) during loading.
  return (
    <ProductCarousel
      heading={heading}
      onNavigate={onNavigate}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={recommendedProducts}
    />
  );
};
